import {
  CheckSquare,
  Edit,
  Edit3,
  ListPlus,
  MoreVertical,
  Square,
} from "lucide-react";
import { IRepaymentHistoryItem } from "../../models/commissionAndRepayment.model";
import { useSelector } from "react-redux";
import { RootState } from "../../store";

type RepaymentHistoryCardProps = {
  item: IRepaymentHistoryItem;
  idx: number;
  id: string;
  repayment: any;
  selectedItems: Record<string, boolean>;
  activeMenu: string | null;
  menuRef: any;
  canUpdateCseStatus: boolean;
  isAccountRole: boolean;
  toggleItemSelection: (repaymentId: string, historyId: string) => void;
  setRepaymentEditIds: (data: any) => void;
  setRepaymentDataForEdit: (data: any) => void;
  setIsEditRepaymentModalOpen: (value: boolean) => void;
  setActiveMenu: (value: string | null) => void;
  setSelectedRepaymentHistoryId: (id: string) => void;
  setCustomFieldModalOpen: (value: boolean) => void;
  setSelectedLogs: (logs: any[]) => void;
  setLogsModalOpen: (value: boolean) => void;
  getStatusBadge: (status: string, label: string) => React.ReactNode;
  formatCurrency: (amount: number) => string;
};

const RepaymentHistoryCard = ({
  item,
  idx,
  id,
  repayment,
  selectedItems,
  activeMenu,
  menuRef,
  canUpdateCseStatus,
  isAccountRole,
  toggleItemSelection,
  setRepaymentEditIds,
  setRepaymentDataForEdit,
  setIsEditRepaymentModalOpen,
  setActiveMenu,
  setSelectedRepaymentHistoryId,
  setCustomFieldModalOpen,
  setSelectedLogs,
  setLogsModalOpen,
  getStatusBadge,
  formatCurrency,
}: RepaymentHistoryCardProps) => {
  const role = useSelector((state: RootState) => state.auth.role);
  const historyId = item._id || `history-${idx}`;
  const key = `${id}-${historyId}`;
  const isSelected = !!selectedItems[key];

  const canShowCheckbox =
    (canUpdateCseStatus && item.cseStatus !== "approved") ||
    (isAccountRole &&
      item.cseStatus === "approved" &&
      item.accountStatus !== "approved");

  const showMenu =
    item.cseStatus === "pending" || (item.logs && item.logs.length > 0);

  const isEditable = role === "admin" || role === "bdm";

  const formatDate = (date?: string | Date | null) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  return (
    <div className="group relative overflow-visible rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all duration-200 hover:shadow-md">
      {/* Top Actions */}
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {canShowCheckbox && (
            <button
              onClick={() => toggleItemSelection(id, historyId)}
              className="rounded-lg p-1 transition hover:bg-slate-100"
            >
              {isSelected ? (
                <CheckSquare className="h-5 w-5 text-sky-600" />
              ) : (
                <Square className="h-5 w-5 text-slate-300 hover:text-slate-500" />
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isEditable && (
            <button
              onClick={(e) => {
                e.stopPropagation();

                setRepaymentEditIds({
                  repaymentId: repayment._id,
                  repaymentHistoryId: item._id || "",
                });

                setRepaymentDataForEdit({
                  fileCharge: item.fileCharge || 0,
                  portalCharge: item.portalCharge || 0,
                  customFields: item.customFields,
                });

                setIsEditRepaymentModalOpen(true);
              }}
              className="rounded-lg p-2 transition hover:bg-slate-100"
            >
              <Edit3 className="h-4 w-4 text-slate-600" />
            </button>
          )}

          {showMenu && (
            <div ref={activeMenu === key ? menuRef : null} className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();

                  setActiveMenu(activeMenu === key ? null : key);
                }}
                className="rounded-lg p-2 transition hover:bg-slate-100"
              >
                <MoreVertical className="h-4 w-4 text-slate-600" />
              </button>

              {activeMenu === key && (
                <div className="absolute right-0 z-20 mt-2 w-44 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  {item.cseStatus === "pending" && (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        console.log("hello");

                        setSelectedRepaymentHistoryId(historyId);
                        setCustomFieldModalOpen(true);
                        setActiveMenu(null);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-sm transition hover:bg-slate-50"
                    >
                      <ListPlus className="h-4 w-4 text-slate-700" />
                      Add Deduction
                    </button>
                  )}

                  {item.logs && item.logs.length > 0 && (
                    <button
                      onClick={() => {
                        setSelectedLogs(item.logs);

                        setLogsModalOpen(true);

                        setActiveMenu(null);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-sm transition hover:bg-slate-50"
                    >
                      <Edit className="h-4 w-4 text-slate-700" />
                      View Logs
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Amount Section */}
      <div className="rounded-lg bg-slate-50 p-3">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-slate-500">Payment Amount</span>

          <span className="text-lg font-semibold text-slate-900">
            {formatCurrency(item.paymentAmount || 0)}
          </span>
        </div>

        <div className="space-y-2">
          {item.fileCharge ? (
            <div className="flex items-start justify-between gap-4 text-sm">
              <span className="text-slate-500">File Charge</span>

              <span className="font-medium text-red-600">
                - {formatCurrency(item.fileCharge)}
              </span>
            </div>
          ) : null}

          {item.portalCharge ? (
            <div className="flex items-start justify-between gap-4 text-sm">
              <span className="text-slate-500">
                {item.totalPortalCharge
                  ? `Portal (${formatCurrency(item.portalCharge)} × ${item.plantSizeKW || 0}kW)`
                  : "Portal Charge"}
              </span>

              <span className="font-medium text-red-600">
                -{formatCurrency(item.totalPortalCharge || item.portalCharge)}
              </span>
            </div>
          ) : null}

          {Array.isArray(item.customFields) &&
            item.customFields.length > 0 &&
            item.customFields.map((field, index) => (
              <div
                key={index}
                className="flex items-start justify-between gap-4 text-sm"
              >
                <span className="capitalize text-slate-500">
                  {field.customFieldName || "Custom Deduction"}
                </span>

                <span className="font-medium text-red-600">
                  -{formatCurrency(field.customFieldValue || 0)}
                </span>
              </div>
            ))}
        </div>

        <div className="my-4 border-t border-dashed border-slate-200" />

        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">
            Final Repayment
          </span>

          <span className="text-xl font-bold text-emerald-600">
            {formatCurrency(item.repaymentAmount || 0)}
          </span>
        </div>
      </div>
      {/* Approved Dates */}
      {(item.cseApprovalDate || item.accountApprovalDate) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {item.cseApprovalDate && (
            <div className="rounded-md bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700 border border-emerald-100">
              <span className="font-medium">CSE:</span>{" "}
              {formatDate(item.cseApprovalDate)}
            </div>
          )}

          {item.accountApprovalDate && (
            <div className="rounded-md bg-sky-50 px-2.5 py-1 text-[11px] text-sky-700 border border-sky-100">
              <span className="font-medium">Account:</span>{" "}
              {formatDate(item.accountApprovalDate)}
            </div>
          )}
        </div>
      )}

      {/* Status */}
      <div className="mt-3 flex flex-col gap-1.5">
        {getStatusBadge(
          item.cseStatus,
          repayment.salesRep?.cseId?.username || "CSE",
        )}

        {getStatusBadge(item.accountStatus || "pending", "Account")}
      </div>
    </div>
  );
};

export default RepaymentHistoryCard;
